import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import { showToast } from 'utils/toasts';
import { ToastTypes } from 'common/types/toasts';
import { track } from 'utils/kitAnalytics';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import { addAccessory, patchAccessory, companionsSchema } from 'containers/KitWorkspaceStorage/api';
import type { ICompanionProduct, ICompanionCategory, IAddAccessoryPayload } from 'containers/KitWorkspaceStorage/types';
import { MIN_QTY } from 'containers/KitWorkspaceStorage/types';

import classNames from 'classnames';
import Button from 'common/components/Button';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import { FormLoader } from 'common/components/Loader';
import BottomSheet from 'common/components/kit-shared/BottomSheet';
import QtyStepper from 'common/components/kit-shared/QtyStepper';
import emptyImage from 'assets/empty-image-square.svg';
import styles from './styles.module.scss';

// 21 = 7 full rows of 3 on desktop, so the last row is not left ragged.
const PAGE_SIZE = 21;
const SEARCH_DEBOUNCE_MS = 350;

export interface ICompanionsTarget {
  kitUuid?: string;
  assemblyUuid?: string;
}

interface ICompanionsCatalogProps {
  target: ICompanionsTarget;
  onClose: () => void;
  onAdded?: () => void;
}

interface IPendingQty { [partNumber: string]: number }

interface ICustomPartFormProps {
  onSubmit: (payload: IAddAccessoryPayload) => void;
  onCancel: () => void;
  disabled: boolean;
}

const CustomPartForm = ({ onSubmit, onCancel, disabled }: ICustomPartFormProps) => {
  const t = useKitTranslate();
  const [partNumber, setPartNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [qty, setQty] = useState(MIN_QTY);

  const submit = useCallback(() => {
    const pn = partNumber.trim();
    if (!pn) {
      showToast(ToastTypes.warning, t('texts.kits.catalog.customPartRequired'));
      return;
    }
    onSubmit({
      partNumber: pn, notes: notes.trim() || undefined, qty, source: 'custom',
    });
  }, [partNumber, notes, qty, onSubmit, t]);

  return (
    <div className={styles.customForm}>
      <label className={styles.customLabel} htmlFor="kit-custom-part-number">
        {t('texts.kits.catalog.customPartLabel')}
        <input
          id="kit-custom-part-number"
          className={styles.searchInput}
          value={partNumber}
          onChange={(e) => setPartNumber(e.currentTarget.value)}
          disabled={disabled}
        />
      </label>
      <label className={styles.customLabel} htmlFor="kit-custom-part-notes">
        {t('texts.kits.catalog.customNotesLabel')}
        <input
          id="kit-custom-part-notes"
          className={styles.searchInput}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          disabled={disabled}
        />
      </label>
      <div className={styles.customActions}>
        <QtyStepper value={qty} onChange={setQty} disabled={disabled} />
        <Button
          schema={ButtonSchema.primary}
          size={ButtonSize.small}
          className={styles.addBtn}
          onClick={submit}
          disabled={disabled}
        >
          {t('texts.kits.catalog.addBtn')}
        </Button>
        <Button
          schema={ButtonSchema.secondary}
          size={ButtonSize.small}
          className={styles.addBtn}
          onClick={onCancel}
          disabled={disabled}
        >
          {t('texts.kits.catalog.cancel')}
        </Button>
      </div>
    </div>
  );
};

/**
 * "Add parts" picker (Variant A): server-side search + paginated all-parts list
 * (no category chips) + a custom-part fallback. Adds picks via addAccessory to
 * the target kit OR standalone assembly. Mobile-first via BottomSheet.
 */
const CompanionsCatalog = ({ target, onClose, onAdded }: ICompanionsCatalogProps) => {
  const t = useKitTranslate();
  const {
    workspaceKey, refetch, kits, standalone,
  } = useKitWorkspace();

  // The kit / assembly the parts are added to: drives the title, the "lines in
  // kit" count, and the per-target "already added" state (qty per part number).
  const targetOwner = useMemo(() => {
    if (target.kitUuid) {
      return kits.find((k) => k.uuid === target.kitUuid) ?? null;
    }
    if (target.assemblyUuid) {
      const inKit = kits
        .flatMap((k) => k.lines)
        .map((l) => l.assembly)
        .find((a) => a?.uuid === target.assemblyUuid);
      return inKit ?? standalone.find((a) => a.uuid === target.assemblyUuid) ?? null;
    }
    return null;
  }, [target, kits, standalone]);

  const ownerName = targetOwner?.name || undefined;
  const title = ownerName
    ? `${t('texts.kits.catalog.title')} — "${ownerName}"`
    : t('texts.kits.catalog.title');

  // Part number -> qty already added to THIS target (kit/assembly). Recomputed
  // after every add via the refetched workspace, so it is tracked per kit.
  const addedQty = useMemo(() => {
    const map = new Map<string, number>();
    (targetOwner?.accessories ?? []).forEach((acc) => {
      map.set(acc.partNumber, (map.get(acc.partNumber) ?? 0) + acc.qty);
    });
    return map;
  }, [targetOwner]);

  const linesInKit = targetOwner?.accessories?.length ?? 0;

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [items, setItems] = useState<ICompanionProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [qty, setQty] = useState<IPendingQty>({});
  const [showCustom, setShowCustom] = useState(false);
  const reqId = useRef(0);

  // Category facets, only rendered when the backend returns them. Selection is
  // multi-choice; an empty selection means "no category filter".
  const [facets, setFacets] = useState<ICompanionCategory[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  // Stable primitive for the reload effect: the array's identity changes on every
  // render, which would turn the effect into an unbounded request loop.
  const selectedKey = useMemo(() => [...selected].sort().join(','), [selected]);

  // Debounce the search term.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(async (nextOffset: number, term: string, cats: string[], append: boolean) => {
    if (!workspaceKey) return;
    const myReq = reqId.current + 1;
    reqId.current = myReq;
    setLoading(true);
    try {
      const res = await companionsSchema(workspaceKey, {
        search: term,
        limit: PAGE_SIZE,
        offset: nextOffset,
        // Omitted entirely when nothing is picked, so the backend sees no filter
        // rather than "match an empty set of categories".
        ...(cats.length ? { category: cats } : {}),
      });
      if (reqId.current !== myReq) return; // stale response
      setItems((prev) => (append ? [...prev, ...res.items] : res.items));
      const results = res.pagination?.total ?? res.items.length;
      setTotal(results);
      setOffset(nextOffset + res.items.length);
      // Keep the FIRST non-empty facet list for the lifetime of the sheet. If a
      // filtered response ever returns only the surviving facets, the chip row
      // must not collapse under the user mid-selection.
      setFacets((prev) => (prev.length ? prev : res.category ?? []));
      if (term || cats.length) {
        track('catalog_searched', {
          workspaceKey, search: term, category: cats, results,
        });
      }
    } catch {
      if (reqId.current === myReq) showToast(ToastTypes.error, t('texts.kits.catalog.loadError'));
    } finally {
      if (reqId.current === myReq) setLoading(false);
    }
  }, [workspaceKey, t]);

  // Reload from scratch when the debounced term, the category selection or the
  // workspace changes. Depends on primitives (selectedKey, NOT the array), so a
  // fresh array identity can never turn this into an unbounded request loop.
  useEffect(() => {
    setItems([]);
    setOffset(0);
    load(0, debounced, selected, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, selectedKey, workspaceKey]);

  const toggleCategory = useCallback((id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  }, []);

  const qtyFor = useCallback((pn: string) => qty[pn] ?? MIN_QTY, [qty]);
  const setQtyFor = useCallback((pn: string, n: number) => setQty((p) => ({ ...p, [pn]: n })), []);

  const doAdd = useCallback(async (payload: IAddAccessoryPayload, key: string) => {
    setAdding(key);
    try {
      // If the part is already in this kit/assembly, bump the existing line's
      // quantity instead of creating a duplicate accessory.
      const existing = (targetOwner?.accessories ?? []).find((a) => a.partNumber === payload.partNumber);
      if (existing) {
        await patchAccessory(existing.uuid, { qty: existing.qty + payload.qty });
      } else {
        await addAccessory(target, payload);
      }
      track('accessory_added', {
        workspaceKey: workspaceKey ?? undefined,
        partNumber: payload.partNumber,
        category: payload.category,
        source: payload.source ?? 'catalog',
        qty: payload.qty,
      });
      showToast(ToastTypes.success, t('texts.kits.catalog.added'));
      await refetch();
      if (onAdded) onAdded();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.catalog.addError'));
    } finally {
      setAdding(null);
    }
  }, [target, targetOwner, workspaceKey, t, refetch, onAdded]);

  const addProduct = useCallback((p: ICompanionProduct) => {
    doAdd({
      partNumber: p.partNumber,
      description: p.description,
      category: p.category,
      imageUrl: p.imageUrl,
      qty: qtyFor(p.partNumber),
      source: 'catalog',
    }, p.partNumber);
  }, [doAdd, qtyFor]);

  const hasMore = items.length < total;
  const noResults = !loading && items.length === 0;

  return (
    <BottomSheet
      title={title}
      subtitle={t('texts.kits.catalog.subtitle')}
      onClose={onClose}
      centered
      wide
      footer={(
        <Button
          schema={ButtonSchema.primary}
          size={ButtonSize.small}
          className={styles.doneBtn}
          onClick={onClose}
        >
          {t('texts.kits.catalog.done')}
        </Button>
      )}
    >
      <div className={styles.searchRow}>
        <input
          className={styles.searchInput}
          type="search"
          placeholder={t('texts.kits.catalog.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          aria-label={t('texts.kits.catalog.searchPlaceholder')}
        />

        {facets.length > 0 && (
          <div className={styles.chipRow} role="group" aria-label={t('texts.kits.catalog.categoryFilter')}>
            <button
              type="button"
              className={classNames(styles.chip, { [styles.chipActive]: selected.length === 0 })}
              onClick={() => setSelected([])}
              aria-pressed={selected.length === 0}
            >
              {t('texts.kits.catalog.allCategories')}
            </button>
            {facets.map((c) => {
              const active = selected.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  className={classNames(styles.chip, { [styles.chipActive]: active })}
                  onClick={() => toggleCategory(c.id)}
                  aria-pressed={active}
                >
                  {c.title || c.value || c.id}
                </button>
              );
            })}
          </div>
        )}

        <span className={styles.count}>
          {t('texts.kits.catalog.showing', { shown: items.length, total, lines: linesInKit })}
        </span>
      </div>

      <div className={styles.grid}>
        {loading && items.length === 0 && <FormLoader />}
        {items.map((p) => {
          const inKitQty = addedQty.get(p.partNumber) ?? 0;
          let addLabel = t('texts.kits.catalog.addBtn');
          if (adding === p.partNumber) addLabel = t('texts.kits.catalog.adding');
          else if (inKitQty > 0) addLabel = t('texts.kits.catalog.addMore');

          return (
            <div key={p.partNumber} className={styles.card}>
              {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
              <img
                className={styles.cardImage}
                src={p.imageUrl || emptyImage}
                alt={p.title || p.partNumber}
                onError={(e) => { e.currentTarget.src = emptyImage; }}
              />
              <div className={styles.cardBody}>
                <div className={styles.partNumber}>{p.partNumber}</div>
                {p.title && <div className={styles.cardTitle}>{p.title}</div>}
                {p.description && <div className={styles.cardDesc}>{p.description}</div>}
                {inKitQty > 0 && (
                  <div className={styles.pills}>
                    <span className={styles.inKitPill}>{t('texts.kits.catalog.inKit', { qty: inKitQty })}</span>
                  </div>
                )}
              </div>
              <div className={styles.cardActions}>
                <QtyStepper
                  value={qtyFor(p.partNumber)}
                  onChange={(n) => setQtyFor(p.partNumber, n)}
                  disabled={!!adding}
                />
                <Button
                  schema={ButtonSchema.primary}
                  size={ButtonSize.small}
                  className={styles.addBtn}
                  onClick={() => addProduct(p)}
                  disabled={!!adding}
                >
                  {addLabel}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {noResults && (
        <div className={styles.noResults}>
          <p>{t('texts.kits.catalog.noResults')}</p>
        </div>
      )}

      {hasMore && !loading && (
        <div className={styles.loadMoreRow}>
          <Button
            schema={ButtonSchema.secondary}
            size={ButtonSize.small}
            onClick={() => load(offset, debounced, selected, true)}
          >
            {t('texts.kits.catalog.loadMore')}
          </Button>
        </div>
      )}

      <div className={styles.customSection}>
        {!showCustom ? (
          <Button
            schema={ButtonSchema.transparent}
            size={ButtonSize.small}
            onClick={() => setShowCustom(true)}
          >
            {t('texts.kits.catalog.customToggle')}
          </Button>
        ) : (
          <CustomPartForm
            disabled={!!adding}
            onSubmit={(payload) => doAdd(payload, payload.partNumber)}
            onCancel={() => setShowCustom(false)}
          />
        )}
      </div>
    </BottomSheet>
  );
};

export default CompanionsCatalog;
